import React, { useEffect, useState } from "react";
import {
  TextInput,
  Select,
  Button,
  Group,
  Grid,
  Col,
  Box,
  createStyles,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { availityBotHandler, restructureArray } from "./Service";
import { getPatientList, getProviderList } from "../Eligibility/EligibiltyService";
import { useMedplum } from "@medplum/react";

import fetchResourceIcon from "../../assets/img/fetchingResourceLogo.svg";
import dbStoringIcon from "../../assets/img/storingDBLogo.svg";
import GenericInfoModal from "../../components/GenericInfoModal";
import availityIcon from "../../assets/img/availity-2700.png";
import { useNavigate } from "react-router-dom";


interface ElementConfig {
  type: string;
  label: string;
  values: any;
  mode: string;
  order: number;
  allowed: boolean;
  required: boolean;
  errorMessage: string;
  pattern?: string;
  maxLength?: number;
  options?: { value: string; label: string }[]; // For dropdowns and radio buttons
}

export interface Configuration {
  type: string;
  categoryId: string;
  categoryValue: string;
  payerId: string;
  version: string;
  elements: {
    [key: string]: ElementConfig;
  };
}

// Define the interface for the grouped data
interface GroupedData {
  provider: { [key: string]: any };
  patient: { [key: string]: any };
  payer: { [key: string]: any };
  subscriber: { [key: string]: any };
  miscellaneous: { [key: string]: any };
}

const modalSteps = [
  { loadText: "Fetching Details", label: "Fetch Details", icon: fetchResourceIcon },
  { loadText: "Generating Pre-Auth Request", label: "Generate Pre-Auth Request", icon: fetchResourceIcon },
  { loadText: `Requesting to 'Availity'`, label: `Request to Availity'`, icon: fetchResourceIcon },
  { loadText: "Converting to FHIR", label: "Convert to FHIR", icon: fetchResourceIcon },
  { loadText: "Storing in HealthConnect", label: "Store in HealthConnect", icon: dbStoringIcon },
];

const useStyles = createStyles(() => ({
  box: {
    backgroundColor: "#fbfbfb",
    padding: "10px",
  },
}));

export const DynamicForm: React.FC<{ configurations: Configuration[], setLoader: any, payerId?: string }> = ({
  configurations, setLoader, payerId
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [providerList, setProviderList] = useState<any[]>([]);
  const [patientList, setPatientList] = useState<any[]>([]);
  const [selectedData, setSelectedData] = useState({
    providerId: '',
    patientId: '',
  });

  const [showInfoModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    sideTitle: "",
    confirmText: "Request Prior Authorization",
    details: [],
    onClose: () => { },
    onConfirm: () => { },
  })

  useEffect(() => {
    Promise.all([
      getPatientList(medplum).then((data: any) => setPatientList(data)),
      getProviderList(medplum).then((data: any) => setProviderList(data)),
    ]);
  }, []);


  const handleChange = (field: string, value: any) => {

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const submitRequest = async (formData: any) => {
    const response = await availityBotHandler(medplum, {
      request: "createServiceReview",
      params: {
        meta: {
          patientId: selectedData.patientId,
          providerId: selectedData.providerId,
          payerId: payerId,
        },
        ...formData,
      }
    });
    return response
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const newErrors: { [key: string]: string } = {};
    // Validate each field
    configurations.forEach((config) => {
      Object.entries(config.elements).forEach(([key, element]) => {
        if (element.required && !formData[key]) {
          newErrors[key] = element.errorMessage;
        } else if (formData[key] && element.pattern) {
          const regex = new RegExp(element.pattern);
          if (!regex.test(formData[key])) {
            newErrors[key] = element.errorMessage;
          }
        }
      });
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Group form data by category (provider, patient, payer, subscriber, etc.)
      const groupedData: GroupedData = {
        provider: {},
        patient: {},
        payer: {},
        subscriber: {},
        miscellaneous: {},
      };

      Object.entries(formData).forEach(([key, value]) => {
        if (key.includes("provider")) {
          groupedData.provider[key] = value;
        } else if (key.includes("patient")) {
          groupedData.patient[key] = value;
        } else if (key.includes("payer")) {
          groupedData.payer[key] = value;
        } else if (key.includes("subscriber")) {
          groupedData.subscriber[key] = value;
        } else {
          groupedData.miscellaneous[key] = value;
        }
      });



      setModalData({
        ...modalData,
        title: formData.patientFirstName + " " + formData.patientLastName,
        sideTitle: "Request to Availity",
        // details: details,
        onClose: () => { },
        onConfirm: () => {
          submitRequest(groupedData).then((data: any) => {
            setModalData((prevData: any) => ({
              ...prevData,
              onClose: () => navigate(`/admin/insurance/prior-auth-dashboard?id=${data.id ?? data.body.id}`)
            }));
          });
        },
      })
      setShowModal(true);
    }


  };

  const setProvider = (val: any) => {
    const providerData = providerList.find((pv: any) => pv.value === val);
    setSelectedData((prev) => ({ ...prev, providerId: val }));
    setFormData((prev) => ({
      ...prev,
      providerFirstName: providerData?.label,
      providerNpi: providerData?.npi,
    }));
  };

  const setPatient = async (id: string) => {
    const patientData = patientList.find((res: any) => res.id === id);
    setSelectedData((prev) => ({ ...prev, patientId: id }));
    setFormData((prev) => ({
      ...prev,
      patientFirstName: patientData?.name?.split(' ')?.[0],
      patientLastName: patientData?.name?.split(' ')?.[1],
      patientBirthDate: patientData?.birthDate ? new Date(patientData?.birthDate) : null
    }));
  };


  // Helper function to render input fields based on the type
  const renderField = (key: string, element: ElementConfig) => {
    if (element.type === "Text") {
      return (
        <TextInput
          label={element.label}
          placeholder={element.label}
          required={element.required}
          value={formData[key]}
          error={errors[key]}
          onChange={(e) => handleChange(key, e.currentTarget.value)}
          maxLength={element.maxLength}
        />
      );
    } else if (element.mode === "DropDown") {
      return (
        <Select
          placeholder={element.label}
          label={element.label}
          required={element.required}
          error={errors[key]}
          value={formData[key]}
          data={restructureArray(element.values)}
          onChange={(value) => handleChange(key, value)}
        />
      );
    } else if (element.type === "Date") {
      return (
        <DateInput
          placeholder={element.label}
          clearable
          label={element.label}
          value={formData[key]} // Use `value` instead of `checked` for controlled input
          onChange={(e) => handleChange(key, e)}
        />
      );
    }
    return null; // Unsupported types can be handled here
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Grid>
          {configurations.some(
            (config) =>
              Object.entries(config.elements)
                .filter(([key]) => key.toLowerCase().includes("provider"))
                .some(([key, element]) => renderField(key, element) !== null) // Check if any valid field exists
          ) && (
              <>
                <Col span={12}>
                  <h3>Provider Information</h3>
                  <Box className={classes.box}>
                    <Box pb={"8px"}>
                      <Select
                        placeholder={"Select Provider"}
                        label={"Select Provider"}
                        required={true}
                        // error={errors[key]}
                        value={selectedData.providerId}
                        data={providerList}
                        onChange={setProvider}
                      />
                    </Box>
                    {configurations.map((config) =>
                      Object.entries(config.elements)
                        .filter(([key]) => key.toLowerCase().includes("provider"))
                        .map(([key, element]) => {
                          const renderedField = renderField(key, element);
                          return renderedField ? (
                            <Box pb={"8px"} key={key}>
                              {renderedField}
                            </Box>
                          ) : null;
                        })
                    )}
                  </Box>
                </Col>
              </>
            )}

          {configurations.some(
            (config) =>
              Object.entries(config.elements)
                .filter(([key]) => key.toLowerCase().includes("patient"))
                .some(([key, element]) => renderField(key, element) !== null) // Check if any valid field exists
          ) && (
              <>
                <Col span={12}>
                  <h3>Patient Information</h3>
                  <Box className={classes.box}>
                    <Box pb={"8px"}>
                      <Select
                        placeholder={"Select Patient"}
                        label={"Select Patient"}
                        required={true}
                        // error={errors[key]}
                        value={selectedData.patientId}
                        data={patientList}
                        onChange={setPatient}
                      />
                    </Box>
                    {configurations.map((config) =>
                      Object.entries(config.elements)
                        .filter(([key]) => key.toLowerCase().includes("patient"))
                        .map(([key, element]) => {
                          const renderedField = renderField(key, element);
                          return renderedField ? (
                            <Box pb={"8px"} key={key}>
                              {renderedField}
                            </Box>
                          ) : null;
                        })
                    )}
                  </Box>
                </Col>
              </>
            )}

          {configurations.some(
            (config) =>
              Object.entries(config.elements)
                .filter(([key]) => key.toLowerCase().includes("payer"))
                .some(([key, element]) => renderField(key, element) !== null) // Check if any valid field exists
          ) && (
              <>
                <Col span={12}>
                  <h3>Payer Information</h3>
                  <Box className={classes.box}>
                    {configurations.map((config) =>
                      Object.entries(config.elements)
                        .filter(([key]) => key.toLowerCase().includes("payer"))
                        .map(([key, element]) => {
                          const renderedField = renderField(key, element);
                          return renderedField ? (
                            <Box pb={"8px"} key={key}>
                              {renderedField}
                            </Box>
                          ) : null;
                        })
                    )}
                  </Box>
                </Col>
              </>
            )}

          {configurations.some(
            (config) =>
              Object.entries(config.elements)
                .filter(([key]) => key.toLowerCase().includes("subscriber"))
                .some(([key, element]) => renderField(key, element) !== null) // Check if any valid field exists
          ) && (
              <>
                <Col span={12}>
                  <h3>Subscriber Information</h3>
                  <Box className={classes.box}>
                    {configurations.map((config) =>
                      Object.entries(config.elements)
                        .filter(([key]) => key.toLowerCase().includes("subscriber"))
                        .map(([key, element]) => {
                          const renderedField = renderField(key, element);
                          return renderedField ? (
                            <Box pb={"8px"} key={key}>
                              {renderedField}
                            </Box>
                          ) : null;
                        })
                    )}
                  </Box>
                </Col>
              </>
            )}

          {configurations.some(
            (config) =>
              Object.entries(config.elements)
                .filter(
                  ([key]) =>
                    !key.toLowerCase().includes("provider") &&
                    !key.toLowerCase().includes("patient") &&
                    !key.toLowerCase().includes("payer") &&
                    !key.toLowerCase().includes("subscriber")
                )
                .some(([key, element]) => renderField(key, element) !== null) // Check if any valid field exists
          ) && (
              <>
                <Col span={12}>
                  <h3>Miscellaneous Information</h3>
                  <Box className={classes.box}>
                    {configurations.map((config) =>
                      Object.entries(config.elements)
                        .filter(
                          ([key]) =>
                            !key.toLowerCase().includes("provider") &&
                            !key.toLowerCase().includes("patient") &&
                            !key.toLowerCase().includes("payer") &&
                            !key.toLowerCase().includes("subscriber")
                        )
                        .map(([key, element]) => {
                          const renderedField = renderField(key, element);
                          return renderedField ? (
                            <Box pb={"8px"} key={key}>
                              {renderedField}
                            </Box>
                          ) : null;
                        })
                    )}
                  </Box>
                </Col>
              </>
            )}
        </Grid>
        <Group position="right" mt="md">
          <Button type="submit">Submit</Button>
        </Group>
      </form>
      {showInfoModal && (
        <GenericInfoModal
          isOpen={showInfoModal}
          steps={modalSteps}
          modalData={modalData}
          onClose={modalData.onClose}
          onConfirm={modalData.onConfirm}
          setIsPatientModalOpen={setShowModal}
          // showStepsImmediately={true}
          // logo={availityIcon}
        />
      )}
    </>
  );
};
